import React, { useEffect, useState } from 'react';
import cfac22 from 'js/library/utils/cfac22';
import { downloadImage } from 'js/library/services/StorageManager.js';
import { Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

import {
  getLastUserInfo,
  encrypt,
  detectmob,
  formatPhone,
  getUrlVariables,
  trackEventMatomo,
  trackEventMatomoVisit,
  startMatomo,
} from 'js/library/utils/helpers';

// Styles
import {
  Typography,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
} from '@mui/material';
import couponImg from 'styles/assets/cuponStar.png';
import mobilePhoneImg from 'styles/assets/mobile-phone.svg';
import placeholderItem from 'styles/assets/placeholder/placeholder.gif';

// Components
import GoBack from 'js/containers/GoBack.js';
import { ModalCoupon } from './ModalCoupon/ModalCoupon';
import { ModalVoucher } from './ModalVoucher/ModalVoucher';

// Apis
import { apiConsumeVoucher } from 'js/library/utils/API/apiConsumeVoucher';
import { getStoreItem } from 'js/library/utils/API/getStoreItem';
import { getUserInfoBasic_v1 } from 'js/library/utils/API/getUserInfo_v1';
import { getWallet_v1 } from 'js/library/utils/API/getWallet_v1';
import LoginModal from 'js/containers/Login/LoginModal';
import { processOrder } from 'js/library/utils/API/processOrder';

export function Store(props) {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Promocao';
    startMatomo();
    trackEventMatomoVisit('Promocao');
  }, []);
  const { t } = useTranslation();
  const isMobile = detectmob();

  const [userInfo, setUserInfo] = useState(getLastUserInfo());
  const [storeInfo, setStoreInfo] = useState(null);
  const [thumbnail, setThumbnail] = useState(placeholderItem);
  const [storeURL, setStoreURL] = useState('');
  const [latitude, setLatitude] = useState(0);
  const [longitude, setLongitude] = useState(0);
  const [totalPoints, setTotalPoints] = useState('');
  const [qrCode, setQrCode] = useState(null);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [orderReply, setOrderReply] = useState(null);
  const [isModalCouponOpen, setIsModalCouponOpen] = useState(false);
  const [id, setId] = useState('');
  const [hasKeywordsConsumption, setKeywordsConsumption] = useState(false);
  const [voucherId, setVoucherId] = useState('');
  const [isModalVoucherOpen, setIsModalVoucherOpen] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(false);

  const [isAnonymous, setIsAnonymous] = useState(false);
  const [openLoginModal, setOpenLoginModal] = useState(false);

  useEffect(() => {
    if (userInfo.triiboId.includes('@anonymous')) {
      setIsAnonymous(true);
    }
  }, [userInfo]);

  useEffect(() => {
    async function fetchUserInfo() {
      try {
        const updated = await getUserInfoBasic_v1(['getUserInfo'], getLastUserInfo());
        setUserInfo(updated);
      } catch (error) {
        console.error('Erro ao buscar informações atualizadas do usuário:', error);
      }
    }

    fetchUserInfo();
  }, []);

  useEffect(() => {
    async function getStore(id) {
      try {
        const dataStore = await getStoreItem(userInfo?.uId, id);

        if (dataStore !== null && dataStore.storeItem !== null) {
          dataStore.thumbnail = dataStore.thumbnail.replace('triibomania-premios/', '');

          const folder = dataStore.type === 'product' ? 'triibomania-premios' : 'promocao';
          const thumb = await downloadImage(folder, dataStore.thumbnail);

          setThumbnail(thumb);

          if (!userInfo.isAnonymous && userInfo.triiboId.indexOf('@anonymous-triibo') < 0) {
            let cellPhone = userInfo.contactList;
            cellPhone = cellPhone.filter((item) => {
              return item.type === 'cellPhone';
            });

            const contactUser = {
              contactList: [...(cellPhone || [])],
            };

            const userInfoUpdated = await getUserInfoBasic_v1(['getUserInfo'], contactUser);

            function hasCommonElements(arr1, arr2) {
              if (arr2.length === 0) return true;

              const set1 = new Set(arr1);
              return arr2.some((element) => set1.has(element));
            }

            const userKeys = userInfoUpdated?.keyWordsSetConsumption ?? [];
            const storeKeys = dataStore?.storeItem?.keyWordsSetConsumption ?? [];

            setKeywordsConsumption(hasCommonElements(userKeys, storeKeys));
          }
          setStoreInfo(dataStore);
          setStoreURL(dataStore.url);
          setQrCode(encrypt(userInfo.uId + '|' + dataStore.templateId, cfac22('CRYPTO_KEY')));
          setId(id);
        } else {
          setStoreInfo(false);
        }
      } catch (error) {
        console.error('Erro ao buscar store:', error);
        setStoreInfo(false);
      }
    }

    const storeId = getUrlVariables().id;
    if (storeId && storeId !== id) getStore(storeId);
    // eslint-disable-next-line
  }, [id, userInfo.uId, storeInfo]);

  useEffect(() => {
    const options = {
      enableHighAccuracy: true,
      timeout: 5000,
      maximumAge: 1000,
    };

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLatitude(position.coords.latitude);
        setLongitude(position.coords.longitude);
      },
      () => {
        setLatitude(-23.605118429151254);
        setLongitude(-46.64470417965819);
      },
      options
    );
  }, []);

  useEffect(() => {
    getWallet_v1(userInfo.uId).then((wallet) => {
      setTotalPoints(wallet.totalPoints);
    });
  }, [userInfo.uId]);

  useEffect(() => {
    const today = Date.now();
    if (
      storeInfo &&
      storeInfo.enableDate <= today && // Ativação é hoje ou antes
      storeInfo.disableDate >= today && // Desativação é hoje ou depois
      storeInfo.isPublished // Publicado
    ) {
      setButtonDisabled(false);
    } else {
      setButtonDisabled(true);
    }
  }, [storeInfo]);

  function orderStore(orderType) {
    if (userInfo?.triiboId?.includes('@anonymous-triibo.com.br')) {
      props.setOpenState(true);
    } else {
      setLoadingOrder(true);

      if (!hasKeywordsConsumption) {
        toast.error(storeInfo?.message ?? 'Você não tem permissão para usar essa promoção', {
          position: 'bottom-right',
        });
        setLoadingOrder(false);
        return;
      } else if (storeInfo.voucherPaymentTemplates) {
        apiConsumeVoucher(userInfo.uId, userInfo.triiboId, storeInfo.id)
          .then((res) => {
            setIsModalVoucherOpen(true);
            setVoucherId(res.result.orderPayment.voucherId);
            setLoadingOrder(false);
          })
          .catch((e) => {
            setLoadingOrder(false);
            toast.error(e.error, { autoClose: 4000 });
          });
      } else {
        const orderObject = {
          itemId: storeInfo.type === 'product' ? storeInfo.id : storeInfo.templateId,
          receiver: userInfo.triiboId,
          requestDate: Date.now(),
          requestLat: latitude,
          requestLon: longitude,
          sender: userInfo.triiboId,
          type: orderType,
        };

        processOrder(userInfo.uId, orderObject, false)
          .then((res) => {
            toast.success(res);
            setLoadingOrder(false);
            setOrderReply(true);
          })
          .catch((error) => {
            console.log(error);
            setLoadingOrder(false);
            setOrderReply(false);
          });
      }
    }
  }
  function handleOpenModal() {
    setIsModalCouponOpen(true);
  }

  function handleCloseModal() {
    setIsModalCouponOpen(false);
  }

  const openAnonymousModal = () => {
    setOpenLoginModal(true);
  };

  function redirectPromotionPage() {
    window.open(storeURL);
  }

  function redirectUserForPage() {
    trackEventMatomo('Promocao', 'tocar', 'botão', 'Infomar');
    localStorage.setItem('loginOption', '3');
    localStorage.setItem('loginRedirect', document.location.href);
  }

  return (
    <div>
      {storeInfo !== null && (
        <ModalCoupon
          storeInfo={storeInfo}
          isOpen={isModalCouponOpen}
          onRequestClose={handleCloseModal}
          storeUrl={storeURL}
        />
      )}

      <ModalVoucher
        voucherId={voucherId}
        isOpen={isModalVoucherOpen}
        setIsOpen={setIsModalVoucherOpen}
      />

      <LoginModal openState={openLoginModal} setOpenState={setOpenLoginModal} />

      {storeInfo === false ? (
        <div style={{ textAlign: 'center' }}>
          <Typography variant="h6">{t('general.did_not_find_were_looking_for')}</Typography>

          <Button
            onClick={() => {
              window.history.back();
              trackEventMatomo('Promocao', 'tocar', 'botao', 'voltar');
            }}
            variant="contained"
            color="primary"
            style={{ textTransform: 'none' }}
          >
            {t('general.back')}
          </Button>
        </div>
      ) : storeInfo === null ? (
        <div style={{ textAlign: 'center' }}>
          <CircularProgress style={{ marginTop: '20px' }} />
        </div>
      ) : (
        <div>
          <GoBack />

          <img src={thumbnail} alt="thumbnail" style={{ width: '100%' }} />

          <Typography style={{ fontWeight: 'bolder', color: 'black' }} variant="h6" gutterBottom>
            {storeInfo.title}
          </Typography>

          <Typography variant="subtitle1" color="secondary" style={{ fontWeight: 'bolder' }}>
            {storeInfo.voucherPaymentTemplates
              ? 'Resgate essa oferta com voucher'
              : storeInfo.type === 'product' && totalPoints && storeInfo
              ? totalPoints < storeInfo.cost
                ? 'Faltam ' + (storeInfo.cost - totalPoints) + ' pontos.'
                : storeInfo.cost + ' pontos'
              : null}
          </Typography>

          <Typography color="secondary" variant="subtitle2" style={{ whiteSpace: 'pre-line' }}>
            {storeInfo.description}
          </Typography>
          <div style={{ marginTop: '30px', textAlign: 'center' }}>
            {userInfo?.triiboId?.includes('@anonymous-triibo.com.br') ? (
              <>
                <Typography
                  color="secondary"
                  variant="subtitle2"
                  style={{ whiteSpace: 'pre-line' }}
                >
                  {t('general.to_take_advantage_provide_name_and_phone')}
                </Typography>

                <Link to="/login" style={{ textDecoration: 'none' }}>
                  <Button
                    onClick={redirectUserForPage}
                    fullWidth={true}
                    style={{ textTransform: 'none' }}
                    color="primary"
                    variant="contained"
                    component="span"
                  >
                    <Typography style={{ fontWeight: 'bold' }} align="center" variant="h5">
                      <span
                        style={{
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <img
                          src={mobilePhoneImg}
                          alt="logo celular"
                          style={{
                            width: '1.8rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginRight: '0.5rem',
                          }}
                        />
                        {t('general.inform')}
                      </span>
                    </Typography>
                  </Button>
                </Link>
              </>
            ) : storeInfo.type === 'businessPartner' ? (
              !isMobile && storeURL.indexOf('tel://') >= 0 ? (
                <Typography variant="subtitle1" color="secondary" gutterBottom display="block">
                  {t('general.call_to')}
                  <br />
                  {formatPhone(storeURL.split('//')[1])}
                </Typography>
              ) : (
                <Button
                  fullWidth={true}
                  style={{ textTransform: 'none', marginTop: '40px' }}
                  color="primary"
                  variant="contained"
                  component="span"
                  onClick={() => {
                    if (isAnonymous) {
                      openAnonymousModal();
                      return;
                    }

                    if (!hasKeywordsConsumption) {
                      toast.error(
                        storeInfo.message ?? 'Você não tem permissão para usar essa promoção',
                        {
                          position: 'bottom-right',
                        }
                      );
                      setLoadingOrder(false);
                    } else {
                      storeInfo.hasOwnProperty('checkoutCode')
                        ? handleOpenModal()
                        : redirectPromotionPage();
                    }
                  }}
                >
                  <Typography style={{ fontWeight: 'bold' }} align="center" variant="h5">
                    <span
                      style={{
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <img
                        src={couponImg}
                        alt=""
                        style={{
                          color: '#FFF',
                          width: '2rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginRight: '1rem',
                        }}
                      />
                      {storeInfo.buttonLabel
                        ? storeInfo.buttonLabel
                        : storeInfo.hasOwnProperty('checkoutCode')
                        ? t('general.rescue_coupon')
                        : t('general.go_to_site')}
                    </span>
                  </Typography>
                </Button>
              )
            ) : storeInfo.type === 'product' ? (
              <>
                {storeInfo.showButtonDescription ? (
                  <Typography align="center" variant="subtitle1" style={{ marginTop: '40px' }}>
                    {storeInfo.buttonDescription
                      ? storeInfo.buttonDescription
                      : t('general.take_advantage_of_your_triibo_points')}
                  </Typography>
                ) : null}
                <Button
                  disabled={loadingOrder || totalPoints < storeInfo.cost || buttonDisabled}
                  onClick={() => {
                    orderStore('store');
                    if (storeInfo.voucherPaymentTemplates) {
                      trackEventMatomo('Produto', 'tocar', 'botao', 'Resgatar com voucher');
                    } else {
                      trackEventMatomo('Produto', 'tocar', 'botao', 'Resgatar produto!');
                    }
                  }}
                  fullWidth={true}
                  style={{ textTransform: 'none' }}
                  color="primary"
                  variant="contained"
                  component="span"
                >
                  {loadingOrder ? (
                    <CircularProgress />
                  ) : (
                    <Typography style={{ fontWeight: 'bold' }} align="center" variant="h5">
                      <span style={{ color: 'white' }}>
                        {storeInfo.voucherPaymentTemplates
                          ? 'Resgatar com voucher'
                          : storeInfo.buttonLabel !== undefined
                          ? storeInfo.buttonLabel
                          : 'Resgatar produto!'}
                      </span>
                    </Typography>
                  )}
                </Button>
              </>
            ) : storeInfo.autoValidate === undefined && storeInfo.showQRCode === undefined ? (
              <QRCodeSVG
                id="QRCode"
                value={qrCode}
                size={140}
                bgColor={'#ffffff'}
                fgColor={'#000000'}
                level={'M'}
              />
            ) : (
              <Button
                fullWidth={true}
                disabled={loadingOrder}
                style={{ textTransform: 'none', marginTop: '40px' }}
                color="primary"
                variant="contained"
                component="span"
                onClick={() => {
                  orderStore('promotion');
                  trackEventMatomo('Produto', 'tocar', 'botao', 'Resgatar produto!');
                }}
              >
                {loadingOrder ? (
                  <CircularProgress />
                ) : (
                  <Typography style={{ fontWeight: 'bold' }} align="center" variant="h5">
                    <span
                      style={{
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <img
                        src={couponImg}
                        alt=""
                        style={{
                          color: '#FFF',
                          width: '2rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginRight: '1rem',
                        }}
                      />
                      {storeInfo.buttonLabel ? storeInfo.buttonLabel : t('general.rescue_coupon')}
                    </span>
                  </Typography>
                )}
              </Button>
            )}
          </div>

          {storeInfo.showGoToPlace ? (
            <div style={{ marginTop: 15, textAlign: 'center' }}>
              <Link
                to={'/estabelecimento/?id=' + storeInfo.establishmentId}
                style={{ textDecoration: 'none' }}
              >
                <Button style={{ textTransform: 'none' }} color="secondary">
                  {storeInfo.goToPlaceLabel
                    ? storeInfo.goToPlaceLabel
                    : t('store.go_to_establishment')}
                </Button>
              </Link>
            </div>
          ) : null}
        </div>
      )}

      <Dialog open={orderReply !== null} onClose={() => setOrderReply(null)}>
        <DialogTitle>
          {orderReply === true && t('general.thanks')}
          {orderReply === false && t('errors.something_went_wrong')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {orderReply === true && t('general.your_redemption_request_has_been_made')}
            {orderReply === false && t('errors.try_later')}
          </DialogContentText>
        </DialogContent>
      </Dialog>
    </div>
  );
}

